import { useEffect, useMemo, useState } from "react";
import type { BmaSiteSummary } from "../api";
import { useClickOutside } from "../utils/useClickOutside";
import styles from "./SiteCombobox.module.css";

// Cap rendered matches so a multi-thousand-site catalogue doesn't
// render a giant list; the operator narrows with the query instead.
const MAX_VISIBLE = 50;

const labelFor = (s: BmaSiteSummary): string =>
  s.address ? `${s.name || s.id} - ${s.address}` : s.name || s.id;

interface Props {
  sites: BmaSiteSummary[];
  // Selected site id, or null when nothing is picked.
  value: string | null;
  onSelect: (siteId: string | null) => void;
  disabled?: boolean;
  // The current project_id, so an "already linked to another project"
  // site can be flagged in the option list.
  currentProjectId: string;
}

// Typeahead combobox for picking a Production site: the search box IS
// the dropdown control (filter as you type), replacing the native
// <select> + separate filter input.
export const SiteCombobox = ({
  sites,
  value,
  onSelect,
  disabled,
  currentProjectId,
}: Props) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapRef = useClickOutside<HTMLDivElement>(open, () => setOpen(false));

  const selected = useMemo(
    () => sites.find((s) => s.id === value) ?? null,
    [sites, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = q
      ? sites.filter((s) =>
          `${s.name} ${s.address} ${s.postcode}`.toLowerCase().includes(q),
        )
      : sites;
    // Always present alphabetically by site NAME (case-insensitive,
    // numeric-aware so "Site 2" precedes "Site 10"). Sites with no name
    // sort to the bottom (by id) rather than interleaving among the
    // named ones, so the named list stays cleanly A-Z.
    return [...matches].sort((a, b) => {
      const an = (a.name || "").trim();
      const bn = (b.name || "").trim();
      if (an && bn)
        return an.localeCompare(bn, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      if (an) return -1;
      if (bn) return 1;
      return a.id.localeCompare(b.id);
    });
  }, [sites, query]);

  const visible = filtered.slice(0, MAX_VISIBLE);

  // Keep the active row in range as the filter narrows.
  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  const choose = (siteId: string) => {
    onSelect(siteId);
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      const active = open ? visible[activeIndex] : undefined;
      const activeTaken =
        active?.linked_project_id && active.linked_project_id !== currentProjectId;
      if (active && !activeTaken) {
        e.preventDefault();
        choose(active.id);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Input shows the live query while open; the selected site's label
  // when closed.
  const inputValue = open ? query : selected ? labelFor(selected) : "";

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <div className={styles.inputRow}>
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          className={styles.input}
          placeholder="Search and select a site by name, address or postcode…"
          value={inputValue}
          disabled={disabled}
          onFocus={() => {
            setQuery("");
            setOpen(true);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
        {selected && !disabled && (
          <button
            type="button"
            className={styles.clearBtn}
            aria-label="Clear selected site"
            onClick={() => {
              onSelect(null);
              setQuery("");
              setOpen(false);
            }}
          >
            ×
          </button>
        )}
      </div>

      {open && !disabled && (
        <div className={styles.listbox} role="listbox">
          {visible.length === 0 ? (
            <div className={styles.empty}>No matching sites.</div>
          ) : (
            <>
              {visible.map((s, i) => {
                // A site that already has a project (other than the one
                // being edited) can't be picked - a site has at most one
                // project. Render it greyed out + non-selectable.
                const taken = Boolean(
                  s.linked_project_id && s.linked_project_id !== currentProjectId,
                );
                return (
                  <div
                    key={s.id}
                    role="option"
                    aria-selected={s.id === value}
                    aria-disabled={taken}
                    className={`${styles.option} ${
                      taken ? styles.optionDisabled : ""
                    } ${!taken && i === activeIndex ? styles.optionActive : ""}`}
                    onMouseEnter={() => {
                      if (!taken) setActiveIndex(i);
                    }}
                    onMouseDown={(e) => {
                      // Prevent the input blur from closing the list
                      // before the click registers.
                      e.preventDefault();
                      if (!taken) choose(s.id);
                    }}
                  >
                    <span className={styles.optionName}>
                      {s.name || s.id}
                      {taken ? " (already has a project)" : ""}
                    </span>
                    <span className={styles.optionMeta}>
                      {s.address || "No address"}
                    </span>
                  </div>
                );
              })}
              {filtered.length > MAX_VISIBLE && (
                <div className={styles.more}>
                  Showing first {MAX_VISIBLE} of {filtered.length}. Keep typing
                  to narrow.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
